import {
  CoveragePriceInfo,
  PriceInfoElement,
} from '@qover-private/protos-web/qover/dojo/pricing/v1/pricing';
import { PaymentRythm } from '@qover/domain';
import type { Variant } from './Terms';

type Coverage = {
  monthlyPremium: number;
  yearlyPremium: number;
};

type Prices = Record<Variant, Coverage> | undefined;
export class PriceInfo {
  priceInfo: Prices;
  basePriceInfo: Prices;
  initialized: boolean;

  constructor(
    initialized: boolean = false,
    priceInfo?: Array<PriceInfoElement>,
    basePriceInfo?: Array<PriceInfoElement>,
  ) {
    this.initialized = initialized;

    this.priceInfo = this.buildPriceInfo(priceInfo);
    this.basePriceInfo = this.buildPriceInfo(basePriceInfo);
  }

  private buildPriceInfo(priceInfoElement?: Array<PriceInfoElement>): Prices {
    if (!priceInfoElement) {
      return;
    }

    return (priceInfoElement?.[0]?.coverages ?? []).reduce((acc, curr) => {
      if (!curr.coverageName || curr.coverageName === 'totalPrice') {
        return acc;
      }

      return {
        ...acc,
        [curr.coverageName as Variant]: this.getPriceFromCoverage(curr),
      };
    }, {} as Record<Variant, Coverage>);
  }

  private getPriceFromCoverage(coverage: CoveragePriceInfo): Coverage {
    return {
      monthlyPremium: coverage.monthlyPremium?.withTaxes ?? 0,
      yearlyPremium: coverage.yearlyPremium?.withTaxes ?? 0,
    };
  }

  getPrice(
    variant?: Variant,
    paymentRythm: PaymentRythm = PaymentRythm.MONTHLY,
  ) {
    if (!variant) {
      return 0;
    }

    return paymentRythm === PaymentRythm.MONTHLY
      ? this.priceInfo?.[variant]?.monthlyPremium ?? 0
      : this.priceInfo?.[variant]?.yearlyPremium ?? 0;
  }

  getBasePrice(
    variant?: Variant,
    paymentRythm: PaymentRythm = PaymentRythm.MONTHLY,
  ) {
    if (!variant) {
      return 0;
    }

    return paymentRythm === PaymentRythm.MONTHLY
      ? this.basePriceInfo?.[variant]?.monthlyPremium ?? 0
      : this.basePriceInfo?.[variant]?.yearlyPremium ?? 0;
  }

  getDiscount(variant?: Variant) {
    const discountPrice = this.getPrice(variant);
    const price = this.getBasePrice(variant);

    if (!price) {
      return 0;
    }

    const ratio = discountPrice / price;
    return Number((1 - ratio).toFixed(2)) * 100;
  }

  getDifferenceWithDiscount(
    variant?: Variant,
    paymentRythm: PaymentRythm = PaymentRythm.MONTHLY,
  ) {
    const discountPrice = this.getPrice(variant, paymentRythm);
    const price = this.getBasePrice(variant, paymentRythm);
    return discountPrice - price;
  }
}
